// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import constants from "@store/constants";
import loadNewLinePricing from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/LoadNewLinePricing";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/UpdateChangeRequestOrder";
import { OrderChangeRequestNewLineModel } from "@store/Framework/Types/ApiModels";
import {
    orderChangeRequestLineReasons,
    orderChangeRequestLineRestoredState,
} from "@store/Library/Components/OrderChangeRequest/OrderChangeRequestLineForm";
import QuickAddProduct, { QuickAddProductPresentationProps } from "@store/Library/Components/QuickAddProduct";
import React, { FC, useContext } from "react";

export interface OrderChangeRequestQuickAddProductProps extends WidgetProps {}

export interface OrderChangeRequestQuickAddProductStyles {
    quickAddProduct?: QuickAddProductPresentationProps;
}

export const orderChangeRequestQuickAddProductStyles: OrderChangeRequestQuickAddProductStyles = {};

const styles = orderChangeRequestQuickAddProductStyles;

export const OrderChangeRequestQuickAddProduct: FC<OrderChangeRequestQuickAddProductProps> = () => {
    const dispatch = useAppDispatch();
    const { value: order } = useContext(OrderStateContext);
    const orderChangeRequest = useAppSelector(state => state.greif.pages.orderChange.changeRequestOrder);

    // Fetch real-time pricing for a new line so it shows Ships From/Full Truck Load/Lead Time/MOQ
    // like existing order lines - this doesn't drive Price/Subtotal, which new lines don't show.
    const fetchNewLinePricing = (productId: string, unitOfMeasure: string, qtyOrdered: number) => {
        dispatch(loadNewLinePricing({ productId, unitOfMeasure, qtyOrdered }));
    };

    const addProductToOrder = (productInfo: ProductInfo, product: ProductModel) => {
        if (!orderChangeRequest) {
            return;
        }

        // Increment quantity if the product being added already exists on the order
        const orderLineId = order?.orderLines?.find(orderLine => orderLine.productId === product.id)?.id;

        if (orderLineId) {
            const existingOrderLine = orderChangeRequest.orderLines?.find(line => line.orderLineId === orderLineId);

            if (existingOrderLine) {
                // Re-enable a canceled line: restore it when the added quantity matches the original,
                // otherwise turn the cancellation into a quantity change with the provided quantity
                const lineUpdate = existingOrderLine.isCanceled
                    ? productInfo.qtyOrdered === Number(existingOrderLine.originalQuantity)
                        ? orderChangeRequestLineRestoredState
                        : {
                              isCanceled: false,
                              newQuantity: productInfo.qtyOrdered,
                              changeReason: orderChangeRequestLineReasons.quantityChange,
                          }
                    : {
                          newQuantity:
                              Number(existingOrderLine.newQuantity || existingOrderLine.originalQuantity) +
                              productInfo.qtyOrdered,
                          changeReason: existingOrderLine.changeReason || orderChangeRequestLineReasons.quantityChange,
                      };

                const orderLines = orderChangeRequest.orderLines.map(line =>
                    line === existingOrderLine ? { ...line, ...lineUpdate } : line,
                );

                dispatch(updateChangeRequestOrder({ orderLines }));

                return;
            }
        }

        // Increment quantity if the product being added already exists on the order change request new lines
        const existingNewLine = orderChangeRequest.newLines?.find(line => line.product.id === product.id);

        if (existingNewLine) {
            const updatedLine: OrderChangeRequestNewLineModel = {
                productInfo,
                product,
                changeNotes: existingNewLine.changeNotes || "",
                newQuantity: Number(existingNewLine.newQuantity) + productInfo.qtyOrdered,
                changeReason: existingNewLine.changeReason || orderChangeRequestLineReasons.newProduct,
            };

            const newLines = orderChangeRequest.newLines?.map(line =>
                line.product.id === product.id ? updatedLine : line,
            );

            dispatch(updateChangeRequestOrder({ newLines }));

            fetchNewLinePricing(product.id, productInfo.unitOfMeasure, Number(updatedLine.newQuantity));

            return;
        }

        // Add the new line if the product doesn't already exist on the order or order change request new lines
        const newLine: OrderChangeRequestNewLineModel = {
            productInfo,
            product,
            changeNotes: "",
            newQuantity: productInfo.qtyOrdered,
            changeReason: orderChangeRequestLineReasons.newProduct,
        };

        dispatch(updateChangeRequestOrder({ newLines: [...(orderChangeRequest.newLines || []), newLine] }));

        fetchNewLinePricing(product.id, productInfo.unitOfMeasure, productInfo.qtyOrdered);
    };

    return (
        <QuickAddProduct
            {...styles.quickAddProduct}
            title={translate("Add items to your order")}
            productSelectorProps={{
                selectButtonTitle: translate("Add to Order"),
                onSelectProduct: addProductToOrder,
                productIsConfigurableMessage: siteMessage("RequestOrderChangeQuickAdd_CannotOrderConfigurable"),
                productIsUnavailableMessage: siteMessage("RequestOrderChangeQuickAdd_ProductIsUnavailable"),
            }}
            data-test-selector="orderChangeRequestQuickAddProduct"
        />
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestQuickAddProduct,
    definition: {
        group: constants.widgetGroup.orderChangeRequest,
        allowedContexts: [constants.pageType.orderChangeRequest],
        displayName: "Order Change Request Quick Add Product",
    },
};

export default widgetModule;
